import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Instagram,
  Youtube,
  Linkedin,
  CalendarIcon,
  Clock,
  Loader2,
  X,
  Library,
  Play,
  FolderOpen,
  Palette,
  ExternalLink,
  Info,
  AlertTriangle,
} from 'lucide-react';

// Simple TikTok icon component
const TikTokIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { format, setHours, setMinutes } from 'date-fns';
import { cn } from '@/lib/utils';

interface SocialAccount {
  _id: string;
  platform: 'instagram' | 'youtube' | 'linkedin' | 'tiktok';
  accountName: string;
}

interface RenderedVideo {
  id: string;
  name: string;
  thumbnailUrl?: string;
  url?: string;
  savedS3Url?: string;
  timestamp: string;
  duration: number;
  status: string;
  renderId?: string;
}

interface SchedulePostModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSchedule: (data: SchedulePostData) => Promise<void>;
  videoUrl?: string;
  thumbnailUrl?: string;
  renderId?: string;
  existingPost?: {
    _id: string;
    title: string;
    caption: string;
    tags?: string[];
    platforms: {
      platform: 'instagram' | 'youtube' | 'linkedin' | 'tiktok';
      accountId: string;
      scheduledTime: string;
    }[];
  };
}

interface TikTokCreatorInfo {
  nickname: string;
  profilePicture: string | null;
  privacy_level_options: string[];
  max_video_post_duration_sec: number;
  comment_disabled: boolean;
  duet_disabled: boolean;
  stitch_disabled: boolean;
}

interface TikTokSettings {
  privacy_level: string;
  disable_comment: boolean;
  disable_duet: boolean;
  disable_stitch: boolean;
  brand_organic_toggle: boolean;
  brand_content_toggle: boolean;
}

interface SchedulePostData {
  postId?: string;
  videoUrl: string;
  thumbnailUrl?: string;
  renderId?: string;
  title: string;
  caption: string;
  tags: string[];
  tiktokSettings?: TikTokSettings;
  platforms: {
    platform: 'instagram' | 'youtube' | 'linkedin' | 'tiktok';
    accountId: string;
    scheduledTime: string;
  }[];
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = [0, 15, 30, 45];

export const SchedulePostModal: React.FC<SchedulePostModalProps> = ({
  isOpen,
  onClose,
  onSchedule,
  videoUrl = '',
  thumbnailUrl,
  renderId,
  existingPost,
}) => {
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Video selection state
  const [showVideoSelector, setShowVideoSelector] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<{
    url: string;
    thumbnailUrl?: string;
    renderId?: string;
    name?: string;
  } | null>(null);
  const [projects, setProjects] = useState<string[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [renderedVideos, setRenderedVideos] = useState<RenderedVideo[]>([]);

  // Form state
  const [title, setTitle] = useState('');
  const [caption, setCaption] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [selectedHour, setSelectedHour] = useState(12);
  const [selectedMinute, setSelectedMinute] = useState(0);
  const [selectedPlatforms, setSelectedPlatforms] = useState<{
    instagram: boolean;
    youtube: boolean;
    linkedin: boolean;
    tiktok: boolean;
  }>({ instagram: false, youtube: false, linkedin: false, tiktok: false });

  // Template state
  const [templates, setTemplates] = useState<any[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');

  // Video preview state
  const [showVideoPreview, setShowVideoPreview] = useState(false);

  // TikTok settings state
  const [tiktokCreatorInfo, setTiktokCreatorInfo] = useState<TikTokCreatorInfo | null>(null);
  const [tiktokLoading, setTiktokLoading] = useState(false);
  const [tiktokPrivacy, setTiktokPrivacy] = useState('');
  const [tiktokDisableComment, setTiktokDisableComment] = useState(false);
  const [tiktokDisableDuet, setTiktokDisableDuet] = useState(false);
  const [tiktokDisableStitch, setTiktokDisableStitch] = useState(false);
  const [tiktokPromotional, setTiktokPromotional] = useState(false);
  const [tiktokBrandOrganic, setTiktokBrandOrganic] = useState(false);
  const [tiktokBrandContent, setTiktokBrandContent] = useState(false);
  const [tiktokConsent, setTiktokConsent] = useState(false);

  // Effective video URL (from props or selected)
  const effectiveVideoUrl = selectedVideo?.url || videoUrl;
  const effectiveThumbnailUrl = selectedVideo?.thumbnailUrl || thumbnailUrl;
  const effectiveRenderId = selectedVideo?.renderId || renderId;

  useEffect(() => {
    if (isOpen) {
      fetchAccounts();
      loadProjects();
      fetchTemplates();
      if (existingPost) {
        setTitle(existingPost.title || '');
        setCaption(existingPost.caption || '');
        setTags(existingPost.tags || []);
        if (existingPost.platforms.length > 0) {
          const firstSchedule = new Date(existingPost.platforms[0].scheduledTime);
          setSelectedDate(firstSchedule);
          setSelectedHour(firstSchedule.getHours());
          setSelectedMinute(firstSchedule.getMinutes());
          setSelectedPlatforms({
            instagram: existingPost.platforms.some((p) => p.platform === 'instagram'),
            youtube: existingPost.platforms.some((p) => p.platform === 'youtube'),
            linkedin: existingPost.platforms.some((p) => p.platform === 'linkedin'),
            tiktok: existingPost.platforms.some((p) => p.platform === 'tiktok'),
          });
        }
      } else {
        // Reset form for new post
        setTitle('');
        setCaption('');
        setTags([]);
        setSelectedDate(new Date());
        setSelectedHour(12);
        setSelectedMinute(0);
        setSelectedPlatforms({ instagram: false, youtube: false, linkedin: false, tiktok: false });
        setSelectedVideo(null);
        setShowVideoSelector(!videoUrl); // Show selector if no video passed in
        setSelectedTemplateId('');
      }
    }
  }, [isOpen, existingPost, videoUrl]);

  const [dbProjects, setDbProjects] = useState<any[]>([]);

  // Load projects - try database first, fall back to localStorage
  const loadProjects = async () => {
    try {
      const response = await fetch('/api/projects', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data.projects && data.projects.length > 0) {
          setDbProjects(data.projects);
          setProjects(data.projects.map((p: any) => p.name));
          const currentProject = localStorage.getItem('currentProject_NLE') || data.projects[0].name;
          setSelectedProject(currentProject);
          return;
        }
      }
    } catch (e) {
      // API not available
    }

    // Fallback to localStorage
    try {
      const saved = localStorage.getItem('projects_NLE');
      const projectList = saved ? JSON.parse(saved) : ['default'];
      setProjects(projectList);
      const currentProject = localStorage.getItem('currentProject_NLE') || 'default';
      setSelectedProject(currentProject);
    } catch (e) {
      setProjects(['default']);
      setSelectedProject('default');
    }
  };

  // Load saved library videos when project changes - try database first
  useEffect(() => {
    if (!selectedProject || !showVideoSelector) return;

    // Try database first
    const dbProject = dbProjects.find(p => p.name === selectedProject);
    if (dbProject && dbProject.savedRenders) {
      const validVideos = dbProject.savedRenders.filter((v: RenderedVideo) =>
        v.savedS3Url || v.url
      ).map((v: RenderedVideo) => ({
        ...v,
        url: v.savedS3Url || v.url,
      }));
      setRenderedVideos(validVideos);
      return;
    }

    // Fallback to localStorage
    try {
      const saved = localStorage.getItem(`project_${selectedProject}_savedRenders`);
      const videos = saved ? JSON.parse(saved) : [];
      const validVideos = videos.filter((v: RenderedVideo) =>
        v.savedS3Url || v.url
      ).map((v: RenderedVideo) => ({
        ...v,
        url: v.savedS3Url || v.url,
      }));
      setRenderedVideos(validVideos);
    } catch (e) {
      setRenderedVideos([]);
    }
  }, [selectedProject, showVideoSelector, dbProjects]);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/social/accounts', {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setAccounts(data.accounts || []);
      }
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await fetch('/api/templates', {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  // Fetch TikTok creator info when TikTok is selected
  const fetchTikTokCreatorInfo = async () => {
    setTiktokLoading(true);
    try {
      const response = await fetch('/api/social/tiktok/creator-info', {
        credentials: 'include',
      });
      if (response.ok) {
        const data: TikTokCreatorInfo = await response.json();
        setTiktokCreatorInfo(data);
        // Pre-set interaction toggles based on creator defaults
        setTiktokDisableComment(data.comment_disabled);
        setTiktokDisableDuet(data.duet_disabled);
        setTiktokDisableStitch(data.stitch_disabled);
      }
    } catch (error) {
      console.error('Failed to fetch TikTok creator info:', error);
    } finally {
      setTiktokLoading(false);
    }
  };

  // Fetch creator info when TikTok is toggled on
  useEffect(() => {
    if (selectedPlatforms.tiktok && !tiktokCreatorInfo) {
      fetchTikTokCreatorInfo();
    }
    if (!selectedPlatforms.tiktok) {
      // Reset TikTok state when unchecked
      setTiktokPrivacy('');
      setTiktokPromotional(false);
      setTiktokBrandOrganic(false);
      setTiktokBrandContent(false);
      setTiktokConsent(false);
    }
  }, [selectedPlatforms.tiktok]);

  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplateId(templateId);
    if (templateId) {
      const template = templates.find((t) => t._id === templateId);
      if (template?.settings?.social) {
        // Apply template social defaults if form is empty
        if (!title && template.settings.social.defaultTitle) {
          setTitle(template.settings.social.defaultTitle);
        }
        if (!caption && template.settings.social.defaultCaption) {
          setCaption(template.settings.social.defaultCaption);
        }
        if (tags.length === 0 && template.settings.social.defaultHashtags?.length > 0) {
          setTags(template.settings.social.defaultHashtags);
        }
      }
    }
  };

  const handleAddTag = () => {
    const trimmed = tagInput.trim().replace(/^#/, '');
    if (trimmed && !tags.includes(trimmed)) {
      setTags([...tags, trimmed]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setTags(tags.filter((t) => t !== tag));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleSubmit = async () => {
    if (!selectedDate) return;
    if (!selectedPlatforms.instagram && !selectedPlatforms.youtube && !selectedPlatforms.linkedin && !selectedPlatforms.tiktok) return;

    setSubmitting(true);
    try {
      const scheduledTime = setMinutes(
        setHours(selectedDate, selectedHour),
        selectedMinute
      ).toISOString();

      const platforms: SchedulePostData['platforms'] = [];

      if (selectedPlatforms.instagram) {
        const instagramAccount = accounts.find((a) => a.platform === 'instagram');
        if (instagramAccount) {
          platforms.push({
            platform: 'instagram',
            accountId: instagramAccount._id,
            scheduledTime,
          });
        }
      }

      if (selectedPlatforms.youtube) {
        const youtubeAccount = accounts.find((a) => a.platform === 'youtube');
        if (youtubeAccount) {
          platforms.push({
            platform: 'youtube',
            accountId: youtubeAccount._id,
            scheduledTime,
          });
        }
      }

      if (selectedPlatforms.linkedin) {
        const linkedinAccount = accounts.find((a) => a.platform === 'linkedin');
        if (linkedinAccount) {
          platforms.push({
            platform: 'linkedin',
            accountId: linkedinAccount._id,
            scheduledTime,
          });
        }
      }

      if (selectedPlatforms.tiktok) {
        const tiktokAccount = accounts.find((a) => a.platform === 'tiktok');
        if (tiktokAccount) {
          platforms.push({
            platform: 'tiktok',
            accountId: tiktokAccount._id,
            scheduledTime,
          });
        }
      }

      // Build TikTok settings if TikTok is selected
      const tiktokSettingsPayload: TikTokSettings | undefined = selectedPlatforms.tiktok
        ? {
            privacy_level: tiktokPrivacy,
            disable_comment: tiktokDisableComment,
            disable_duet: tiktokDisableDuet,
            disable_stitch: tiktokDisableStitch,
            brand_organic_toggle: tiktokBrandOrganic,
            brand_content_toggle: tiktokBrandContent,
          }
        : undefined;

      await onSchedule({
        postId: existingPost?._id,
        videoUrl: effectiveVideoUrl,
        thumbnailUrl: effectiveThumbnailUrl,
        renderId: effectiveRenderId,
        title,
        caption,
        tags,
        tiktokSettings: tiktokSettingsPayload,
        platforms,
      });

      onClose();
    } catch (error) {
      console.error('Failed to schedule post:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const instagramAccount = accounts.find((a) => a.platform === 'instagram');
  const youtubeAccount = accounts.find((a) => a.platform === 'youtube');
  const linkedinAccount = accounts.find((a) => a.platform === 'linkedin');
  const tiktokAccount = accounts.find((a) => a.platform === 'tiktok');
  const hasAnyAccount = instagramAccount || youtubeAccount || linkedinAccount || tiktokAccount;
  // TikTok-specific validation
  const tiktokValid = !selectedPlatforms.tiktok || (
    tiktokPrivacy !== '' &&
    tiktokConsent &&
    (!tiktokPromotional || (tiktokBrandOrganic || tiktokBrandContent)) &&
    !(tiktokBrandContent && tiktokPrivacy === 'SELF_ONLY')
  );

  const canSubmit =
    hasAnyAccount &&
    effectiveVideoUrl &&
    (selectedPlatforms.instagram || selectedPlatforms.youtube || selectedPlatforms.linkedin || selectedPlatforms.tiktok) &&
    selectedDate &&
    title.trim() &&
    tiktokValid;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle>
            {existingPost ? 'Edit Scheduled Post' : 'Schedule Post'}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : !hasAnyAccount ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-4">
              No social accounts connected. Please connect at least one account to schedule posts.
            </p>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        ) : (
          <div className="space-y-4 w-full max-w-full overflow-hidden">
            {/* Video Selection */}
            <div className="space-y-2">
              <Label>Video</Label>
              {effectiveVideoUrl ? (
                <Card className="p-3 w-full max-w-full overflow-hidden">
                  {/* Video Preview Player */}
                  {showVideoPreview ? (
                    <div className="space-y-2">
                      <video
                        src={effectiveVideoUrl}
                        controls
                        autoPlay
                        className="w-full rounded aspect-video bg-black"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full"
                        onClick={() => setShowVideoPreview(false)}
                      >
                        Hide Preview
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 w-full max-w-full overflow-hidden">
                      <div
                        className="w-20 h-14 bg-muted rounded overflow-hidden flex-shrink-0 cursor-pointer relative group"
                        onClick={() => setShowVideoPreview(true)}
                        title="Click to preview video"
                      >
                        {effectiveThumbnailUrl ? (
                          <img
                            src={effectiveThumbnailUrl}
                            alt="Video thumbnail"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Play className="h-6 w-6 text-muted-foreground" />
                          </div>
                        )}
                        {/* Play overlay on hover */}
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <Play className="h-6 w-6 text-white fill-white" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <p className="text-sm font-medium truncate max-w-full">
                          {selectedVideo?.name || 'Selected Video'}
                        </p>
                        <p className="text-xs text-muted-foreground truncate max-w-full break-all">
                          {effectiveVideoUrl.split('/').pop()}
                        </p>
                        <button
                          type="button"
                          className="text-xs text-primary hover:underline mt-0.5"
                          onClick={() => setShowVideoPreview(true)}
                        >
                          Preview video
                        </button>
                      </div>
                      {!videoUrl && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedVideo(null);
                            setShowVideoSelector(true);
                            setShowVideoPreview(false);
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  )}
                </Card>
              ) : (
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => setShowVideoSelector(true)}
                >
                  <Library className="h-4 w-4 mr-2" />
                  Select Video from Saved Library
                </Button>
              )}

              {/* Inline Video Browser */}
              {showVideoSelector && !effectiveVideoUrl && (
                <Card className="p-3 space-y-3">
                  <div className="flex items-center gap-2">
                    <FolderOpen className="h-4 w-4 text-muted-foreground" />
                    <Select value={selectedProject} onValueChange={setSelectedProject}>
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Select project" />
                      </SelectTrigger>
                      <SelectContent>
                        {projects.map((project) => (
                          <SelectItem key={project} value={project}>
                            {project}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <ScrollArea className="h-[200px]">
                    {renderedVideos.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full text-center py-8">
                        <Library className="h-8 w-8 text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">
                          No saved videos in this project
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Save renders to Library in Video Enhancer
                        </p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        {renderedVideos.map((video) => (
                          <Card
                            key={video.id}
                            className="cursor-pointer hover:ring-1 hover:ring-primary overflow-hidden"
                            onClick={() => {
                              setSelectedVideo({
                                url: video.url!,
                                thumbnailUrl: video.thumbnailUrl,
                                renderId: video.renderId,
                                name: video.name,
                              });
                              setShowVideoSelector(false);
                            }}
                          >
                            <div className="aspect-video bg-muted relative">
                              {video.thumbnailUrl ? (
                                <img
                                  src={video.thumbnailUrl}
                                  alt={video.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Play className="h-6 w-6 text-muted-foreground" />
                                </div>
                              )}
                              <div className="absolute bottom-1 right-1 bg-black/70 px-1 py-0.5 rounded text-[10px] text-white">
                                {Math.round(video.duration)}s
                              </div>
                            </div>
                            <div className="p-1.5">
                              <p className="text-xs font-medium truncate">{video.name}</p>
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}
                  </ScrollArea>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full"
                    onClick={() => setShowVideoSelector(false)}
                  >
                    Cancel
                  </Button>
                </Card>
              )}
            </div>

            {/* Template Selection */}
            {templates.length > 0 && (
              <div className="space-y-2">
                <Label>Apply Template (Optional)</Label>
                <Select value={selectedTemplateId || 'none'} onValueChange={(v) => handleTemplateChange(v === 'none' ? '' : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a template for defaults..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No template</SelectItem>
                    {templates.map((template) => (
                      <SelectItem key={template._id} value={template._id}>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{
                              backgroundColor: template.settings?.colors?.primary || '#3b82f6',
                            }}
                          />
                          {template.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Templates can pre-fill title, caption, and hashtags
                </p>
              </div>
            )}

            {/* Platform Selection */}
            <div className="space-y-2">
              <Label>Platforms</Label>
              <div className="flex gap-4">
                {instagramAccount && (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="instagram"
                      checked={selectedPlatforms.instagram}
                      onCheckedChange={(checked) =>
                        setSelectedPlatforms((prev) => ({
                          ...prev,
                          instagram: checked as boolean,
                        }))
                      }
                    />
                    <label
                      htmlFor="instagram"
                      className="flex items-center gap-2 text-sm cursor-pointer"
                    >
                      <Instagram className="h-4 w-4 text-pink-500" />
                      Instagram
                    </label>
                  </div>
                )}
                {youtubeAccount && (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="youtube"
                      checked={selectedPlatforms.youtube}
                      onCheckedChange={(checked) =>
                        setSelectedPlatforms((prev) => ({
                          ...prev,
                          youtube: checked as boolean,
                        }))
                      }
                    />
                    <label
                      htmlFor="youtube"
                      className="flex items-center gap-2 text-sm cursor-pointer"
                    >
                      <Youtube className="h-4 w-4 text-red-500" />
                      YouTube
                    </label>
                  </div>
                )}
                {linkedinAccount && (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="linkedin"
                      checked={selectedPlatforms.linkedin}
                      onCheckedChange={(checked) =>
                        setSelectedPlatforms((prev) => ({
                          ...prev,
                          linkedin: checked as boolean,
                        }))
                      }
                    />
                    <label
                      htmlFor="linkedin"
                      className="flex items-center gap-2 text-sm cursor-pointer"
                    >
                      <Linkedin className="h-4 w-4 text-blue-600" />
                      LinkedIn
                    </label>
                  </div>
                )}
                {tiktokAccount && (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="tiktok"
                      checked={selectedPlatforms.tiktok}
                      onCheckedChange={(checked) =>
                        setSelectedPlatforms((prev) => ({
                          ...prev,
                          tiktok: checked as boolean,
                        }))
                      }
                    />
                    <label
                      htmlFor="tiktok"
                      className="flex items-center gap-2 text-sm cursor-pointer"
                    >
                      <TikTokIcon className="h-4 w-4" />
                      TikTok
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* TikTok Settings - All 5 Required UX Points */}
            {selectedPlatforms.tiktok && tiktokAccount && (
              <div className="border rounded-lg p-4 space-y-4 bg-muted/30">
                <div className="flex items-center gap-2">
                  <TikTokIcon className="h-5 w-5" />
                  <span className="font-medium text-sm">TikTok Post Settings</span>
                </div>

                {tiktokLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    <span className="text-sm text-muted-foreground">Loading TikTok account info...</span>
                  </div>
                ) : tiktokCreatorInfo ? (
                  <>
                    {/* Point 1: Creator Info */}
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground uppercase tracking-wide">1. Account Info</Label>
                      <div className="flex items-center gap-3 p-2 bg-background rounded border">
                        {tiktokCreatorInfo.profilePicture ? (
                          <img
                            src={tiktokCreatorInfo.profilePicture}
                            alt={tiktokCreatorInfo.nickname}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                            <TikTokIcon className="h-5 w-5" />
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-medium">{tiktokCreatorInfo.nickname}</p>
                          <p className="text-xs text-muted-foreground">
                            Max video duration: {Math.floor(tiktokCreatorInfo.max_video_post_duration_sec / 60)}m {tiktokCreatorInfo.max_video_post_duration_sec % 60}s
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Point 2: Metadata — Privacy + Interactions */}
                    <div className="space-y-3">
                      <Label className="text-xs text-muted-foreground uppercase tracking-wide">2. Privacy & Interactions</Label>

                      {/* Privacy Level Select — no default */}
                      <div className="space-y-1">
                        <Label htmlFor="tiktok-privacy" className="text-sm">Who can view this video</Label>
                        <Select value={tiktokPrivacy} onValueChange={setTiktokPrivacy}>
                          <SelectTrigger id="tiktok-privacy">
                            <SelectValue placeholder="Select privacy level..." />
                          </SelectTrigger>
                          <SelectContent>
                            {tiktokCreatorInfo.privacy_level_options.map((opt) => (
                              <SelectItem key={opt} value={opt}>
                                {opt === 'PUBLIC_TO_EVERYONE' ? 'Everyone' :
                                 opt === 'MUTUAL_FOLLOW_FRIENDS' ? 'Friends' :
                                 opt === 'FOLLOWER_OF_CREATOR' ? 'Followers' :
                                 opt === 'SELF_ONLY' ? 'Only me' : opt}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {!tiktokPrivacy && (
                          <p className="text-xs text-destructive">Privacy level is required</p>
                        )}
                      </div>

                      {/* Interaction Toggles */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="tiktok-comment" className="text-sm">Allow comments</Label>
                          <Switch
                            id="tiktok-comment"
                            checked={!tiktokDisableComment}
                            onCheckedChange={(checked) => setTiktokDisableComment(!checked)}
                            disabled={tiktokCreatorInfo.comment_disabled}
                          />
                        </div>
                        {tiktokCreatorInfo.comment_disabled && (
                          <p className="text-xs text-muted-foreground">Comments are disabled for this account</p>
                        )}

                        <div className="flex items-center justify-between">
                          <Label htmlFor="tiktok-duet" className="text-sm">Allow Duet</Label>
                          <Switch
                            id="tiktok-duet"
                            checked={!tiktokDisableDuet}
                            onCheckedChange={(checked) => setTiktokDisableDuet(!checked)}
                            disabled={tiktokCreatorInfo.duet_disabled}
                          />
                        </div>
                        {tiktokCreatorInfo.duet_disabled && (
                          <p className="text-xs text-muted-foreground">Duet is disabled for this account</p>
                        )}

                        <div className="flex items-center justify-between">
                          <Label htmlFor="tiktok-stitch" className="text-sm">Allow Stitch</Label>
                          <Switch
                            id="tiktok-stitch"
                            checked={!tiktokDisableStitch}
                            onCheckedChange={(checked) => setTiktokDisableStitch(!checked)}
                            disabled={tiktokCreatorInfo.stitch_disabled}
                          />
                        </div>
                        {tiktokCreatorInfo.stitch_disabled && (
                          <p className="text-xs text-muted-foreground">Stitch is disabled for this account</p>
                        )}
                      </div>
                    </div>

                    {/* Point 3: Commercial Content Disclosure */}
                    <div className="space-y-3">
                      <Label className="text-xs text-muted-foreground uppercase tracking-wide">3. Content Disclosure</Label>

                      <div className="flex items-center justify-between">
                        <div>
                          <Label htmlFor="tiktok-promotional" className="text-sm">Promotional content</Label>
                          <p className="text-xs text-muted-foreground">Indicate if this is promotional or sponsored</p>
                        </div>
                        <Switch
                          id="tiktok-promotional"
                          checked={tiktokPromotional}
                          onCheckedChange={(checked) => {
                            setTiktokPromotional(checked);
                            if (!checked) {
                              setTiktokBrandOrganic(false);
                              setTiktokBrandContent(false);
                            }
                          }}
                        />
                      </div>

                      {tiktokPromotional && (
                        <div className="pl-4 border-l-2 space-y-2">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="tiktok-brand-organic"
                              checked={tiktokBrandOrganic}
                              onCheckedChange={(checked) => setTiktokBrandOrganic(checked as boolean)}
                            />
                            <label htmlFor="tiktok-brand-organic" className="text-sm cursor-pointer">
                              Your brand — You are promoting yourself or your own business
                            </label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="tiktok-brand-content"
                              checked={tiktokBrandContent}
                              onCheckedChange={(checked) => {
                                const val = checked as boolean;
                                setTiktokBrandContent(val);
                                // Branded content cannot use SELF_ONLY
                                if (val && tiktokPrivacy === 'SELF_ONLY') {
                                  setTiktokPrivacy('');
                                }
                              }}
                            />
                            <label htmlFor="tiktok-brand-content" className="text-sm cursor-pointer">
                              Branded content — You are promoting another brand or third party
                            </label>
                          </div>
                          {!tiktokBrandOrganic && !tiktokBrandContent && (
                            <p className="text-xs text-destructive">Select at least one disclosure type</p>
                          )}
                          {tiktokBrandContent && tiktokPrivacy === 'SELF_ONLY' && (
                            <Alert variant="destructive" className="py-2">
                              <AlertTriangle className="h-4 w-4" />
                              <AlertDescription className="text-xs">
                                Branded content cannot be set to "Only me" privacy. Please select a different privacy level.
                              </AlertDescription>
                            </Alert>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Point 4: Compliance Links */}
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground uppercase tracking-wide">4. Compliance</Label>
                      <div className="space-y-1">
                        <a
                          href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-sm text-primary hover:underline"
                        >
                          <ExternalLink className="h-3 w-3" />
                          Music Usage Confirmation
                        </a>
                        {tiktokBrandContent && (
                          <a
                            href="https://www.tiktok.com/legal/page/global/bc-policy/en"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-sm text-primary hover:underline"
                          >
                            <ExternalLink className="h-3 w-3" />
                            Branded Content Policy
                          </a>
                        )}
                      </div>
                    </div>

                    {/* Point 5: User Consent */}
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground uppercase tracking-wide">5. Consent</Label>
                      <div className="flex items-start space-x-2">
                        <Checkbox
                          id="tiktok-consent"
                          checked={tiktokConsent}
                          onCheckedChange={(checked) => setTiktokConsent(checked as boolean)}
                        />
                        <label htmlFor="tiktok-consent" className="text-xs leading-relaxed cursor-pointer">
                          I confirm this video is ready to upload to TikTok. By posting, I agree to TikTok's{' '}
                          <a href="https://www.tiktok.com/legal/page/global/terms-of-service/en" target="_blank" rel="noopener noreferrer" className="text-primary underline">Terms of Service</a>
                          {' '}and{' '}
                          <a href="https://www.tiktok.com/legal/page/global/privacy-policy/en" target="_blank" rel="noopener noreferrer" className="text-primary underline">Privacy Policy</a>.
                        </label>
                      </div>
                      {!tiktokConsent && (
                        <p className="text-xs text-destructive">Consent is required to post to TikTok</p>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="flex items-center gap-2 py-2 text-sm text-destructive">
                    <AlertTriangle className="h-4 w-4" />
                    Failed to load TikTok account info.{' '}
                    <button className="underline" onClick={fetchTikTokCreatorInfo}>Retry</button>
                  </div>
                )}
              </div>
            )}

            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Post title..."
              />
            </div>

            {/* Caption */}
            <div className="space-y-2">
              <Label htmlFor="caption">Caption / Description</Label>
              <Textarea
                id="caption"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Write your caption..."
                rows={3}
              />
            </div>

            {/* Tags (for YouTube) */}
            {selectedPlatforms.youtube && (
              <div className="space-y-2">
                <Label htmlFor="tags">Tags (YouTube)</Label>
                <div className="flex gap-2">
                  <Input
                    id="tags"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Add tags..."
                  />
                  <Button type="button" variant="outline" onClick={handleAddTag}>
                    Add
                  </Button>
                </div>
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {tags.map((tag) => (
                      <Badge key={tag} variant="secondary" className="gap-1">
                        #{tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="ml-1 hover:text-destructive"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Schedule Date & Time */}
            <div className="space-y-2">
              <Label>Schedule Time</Label>
              <div className="flex flex-wrap gap-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'min-w-[180px] flex-1 justify-start text-left font-normal',
                        !selectedDate && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
                      {selectedDate ? format(selectedDate, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>

                <div className="flex items-center gap-2">
                  <Select
                    value={selectedHour.toString()}
                    onValueChange={(v) => setSelectedHour(parseInt(v))}
                  >
                    <SelectTrigger className="w-[70px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {HOURS.map((h) => (
                        <SelectItem key={h} value={h.toString()}>
                          {h.toString().padStart(2, '0')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <span className="text-muted-foreground">:</span>

                  <Select
                    value={selectedMinute.toString()}
                    onValueChange={(v) => setSelectedMinute(parseInt(v))}
                  >
                    <SelectTrigger className="w-[70px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MINUTES.map((m) => (
                        <SelectItem key={m} value={m.toString()}>
                          {m.toString().padStart(2, '0')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {selectedDate && (
                <p className="text-xs text-muted-foreground">
                  Scheduled for{' '}
                  {format(
                    setMinutes(setHours(selectedDate, selectedHour), selectedMinute),
                    'PPP p'
                  )}
                </p>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || submitting}>
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {existingPost ? 'Update' : 'Schedule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
